import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { 
  FileText, 
  CheckCircle, 
  Play, 
  Code2, 
  Eye, 
  Zap, 
  Maximize2,
  Search,
  Upload,
  Plus,
  LayoutGrid,
  FileCode
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Command Palette - Updated for Graph-First Workflow
 * 
 * Primary actions: Create Machine (minimal), Open Caps & Effects
 * DSL creation moved to Import submenu as advanced option
 */
const CommandPalette = ({ open, onOpenChange, onCommand }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  const commands = [
    // Primary creation
    { id: 'create', label: 'Create Machine', description: 'New machine with initial state', icon: Plus, keys: ['N'], category: 'create' },
    
    // Authority management
    { id: 'open-caps-effects', label: 'Open Capabilities & Effects', description: 'Manage machine authority', icon: Zap, keys: ['C', 'E'], category: 'navigate' },
    { id: 'add-effect', label: 'Add Effect', description: 'Create new effect', icon: Zap, keys: ['A', 'E'], category: 'create' },
    
    // Graph operations
    { id: 'graph-view', label: 'Switch to Graph', description: 'Visual authoring surface', icon: LayoutGrid, keys: ['G'], category: 'navigate' },
    
    // Import (DSL is secondary)
    { id: 'import', label: 'Import Machine', description: 'Import from DSL or file', icon: Upload, keys: ['I'], category: 'import' },
    { id: 'import-dsl', label: 'Import from DSL (Advanced)', description: 'Create machine from DSL text', icon: FileCode, keys: ['I', 'D'], category: 'import' },
    
    // Actions
    { id: 'validate', label: 'Validate Now', description: 'Check machine validity', icon: CheckCircle, keys: ['V'], category: 'action' },
    { id: 'simulate', label: 'Run Simulation Step', description: 'Step through execution', icon: Play, keys: ['S'], category: 'action' },
    
    // Export
    { id: 'export-ts', label: 'Export TypeScript', description: 'Compile to TypeScript', icon: Code2, keys: ['E', 'T'], category: 'export' },
    { id: 'export-schema', label: 'Export JSON Schema', description: 'Export JSON schema', icon: Code2, keys: ['E', 'S'], category: 'export' },
    { id: 'export-graph', label: 'Export Graph Data', description: 'Export graph structure', icon: Code2, keys: ['E', 'G'], category: 'export' },
    
    // View toggles
    { id: 'toggle-inspector', label: 'Toggle Inspector', description: 'Show/hide right panel', icon: Eye, keys: ['I'], category: 'view' },
    { id: 'focus-mode', label: 'Toggle Focus Mode', description: 'Immersive graph editing', icon: Maximize2, keys: ['F'], category: 'view' },
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Group by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  const categoryLabels = {
    create: 'Create',
    navigate: 'Navigate',
    import: 'Import',
    action: 'Actions',
    export: 'Export',
    view: 'View'
  };

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected(prev => (prev + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selected]) {
          onCommand(filteredCommands[selected].id);
          onOpenChange(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selected, filteredCommands, onCommand, onOpenChange]);

  let flatIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px] p-0 gap-0 bg-[#0a0e27] border-white/10"
        data-testid="command-palette"
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Command Palette</DialogTitle>
        </VisuallyHidden>
        <div className="flex items-center border-b border-white/10 px-3 py-2">
          <Search className="w-4 h-4 text-gray-500 mr-2" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected(0);
            }}
            placeholder="Type a command or search..."
            className="border-0 bg-transparent focus-visible:ring-0 text-sm"
            data-testid="command-palette-input"
          />
        </div>

        <ScrollArea className="max-h-[400px]">
          <div className="py-2">
            {filteredCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No commands found
              </div>
            ) : (
              Object.entries(groupedCommands).map(([category, cmds]) => (
                <div key={category}>
                  <div className="px-4 py-1.5 text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    {categoryLabels[category] || category}
                  </div>
                  {cmds.map((cmd) => {
                    flatIndex++;
                    const currentIndex = flatIndex;
                    const Icon = cmd.icon;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => {
                          onCommand(cmd.id);
                          onOpenChange(false);
                        }}
                        className={cn(
                          "w-full flex items-center justify-between px-4 py-2 text-sm transition-colors",
                          currentIndex === selected
                            ? "bg-blue-500/20 text-blue-300"
                            : "text-gray-300 hover:bg-white/5"
                        )}
                        data-testid={`command-${cmd.id}`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className={cn(
                            "w-4 h-4",
                            cmd.category === 'create' && "text-green-400",
                            cmd.category === 'navigate' && cmd.id.includes('caps') && "text-yellow-400"
                          )} />
                          <div className="text-left">
                            <div>{cmd.label}</div>
                            {cmd.description && (
                              <div className="text-[10px] text-gray-500">{cmd.description}</div>
                            )}
                          </div>
                        </div>
                        {cmd.keys && (
                          <div className="flex items-center space-x-1">
                            {cmd.keys.map((key, i) => (
                              <kbd
                                key={i}
                                className="px-1.5 py-0.5 text-[10px] font-mono bg-black/50 border border-white/10 rounded"
                              >
                                {key}
                              </kbd>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-white/10 px-4 py-2 text-[10px] text-gray-500 flex items-center justify-between">
          <span>Navigate with ↑↓ arrows</span>
          <span>Press <kbd className="px-1 py-0.5 bg-black/50 border border-white/10 rounded">Enter</kbd> to select</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
